import React, { useSyncExternalStore, useState, useContext, createContext, useId } from 'react';
import { Button as RsuiteButton, Nav as RsuiteNav } from 'rsuite';
import {
  ResponsiveContainer,
  AreaChart as RechartsAreaChart,
  LineChart as RechartsLineChart,
  PieChart as RechartsPieChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  Legend as RechartsLegend,
  Area,
  Line,
  Pie,
  Cell,
} from 'recharts';

import { defaultCardStyle, resolveColor, resolveColorDark, resolveColorTint, tremorBadgeStyle } from '../styles';

// Drop-in replacements for the Tremor layout/typography primitives
// (Flex, Card, Grid, Col, Title, Subtitle, Metric, Text, Bold) so call sites
// only need to change their import source, not their JSX. Tailwind utility
// classNames passed in by callers (space-x-4, mt-3, truncate, gap-4, ...)
// are left untouched and keep working exactly as before — only the identity
// of the wrapping element/props changes. Behavior is modeled directly off
// @tremor/react's own source (v3.16), not guessed, to avoid visual drift.
//
// Button/Icon/Badge (Batch 2) are real rsuite-backed (or plain-element)
// components rather than plain divs, since that's genuinely the target here
// — but every call site everywhere in this codebase passes `icon=` as a bare
// react-icon component reference (Tremor's own shape), never as JSX, so
// renderIcon() below normalizes either shape without requiring any call site
// to change, the same zero-JSX-diff approach as Batch 1.

const BREAKPOINTS = { sm: 640, md: 768, lg: 1024, xl: 1280 };

const getActiveBreakpoint = () => {
  if (typeof window === 'undefined') return 'base';
  if (window.innerWidth >= BREAKPOINTS.xl) return 'xl';
  if (window.innerWidth >= BREAKPOINTS.lg) return 'lg';
  if (window.innerWidth >= BREAKPOINTS.md) return 'md';
  if (window.innerWidth >= BREAKPOINTS.sm) return 'sm';
  return 'base';
};

// Tracks which Tailwind breakpoint is active so Grid/Col can reproduce
// Tremor's mobile-first numItemsSm/numItemsMd/numItemsLg cascade without
// relying on Tailwind's own generated CSS (which Batch 7 may remove).
//
// Grid/Col are used dozens of times across the dashboard, and each used to
// run its own useEffect subscribing its own 3 matchMedia listeners — an
// unnecessary N×3 subscription cost for state (which breakpoint is active)
// that's identical for every instance. This is one module-level store
// shared by every Grid/Col via useSyncExternalStore instead: the real
// matchMedia subscription is created lazily on the first subscriber and
// torn down once the last one unmounts.
const breakpointListeners = new Set();
let breakpointMediaCleanup = null;

const notifyBreakpointListeners = () => breakpointListeners.forEach((listener) => listener());

const ensureBreakpointSubscription = () => {
  // jsdom (the Jest test environment) doesn't implement matchMedia — skip
  // live breakpoint tracking there and keep reading the initial value.
  if (breakpointMediaCleanup || typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

  const queries = Object.values(BREAKPOINTS).map((px) => window.matchMedia(`(min-width: ${px}px)`));

  // Older Safari (<14) only implements the deprecated addListener/
  // removeListener pair — MediaQueryList has no addEventListener there.
  const supportsEventListener = typeof queries[0].addEventListener === 'function';
  if (supportsEventListener) {
    queries.forEach((mql) => mql.addEventListener('change', notifyBreakpointListeners));
  } else {
    queries.forEach((mql) => mql.addListener(notifyBreakpointListeners));
  }

  breakpointMediaCleanup = () => {
    if (supportsEventListener) {
      queries.forEach((mql) => mql.removeEventListener('change', notifyBreakpointListeners));
    } else {
      queries.forEach((mql) => mql.removeListener(notifyBreakpointListeners));
    }
    breakpointMediaCleanup = null;
  };
};

const subscribeToBreakpoint = (listener) => {
  ensureBreakpointSubscription();
  breakpointListeners.add(listener);
  return () => {
    breakpointListeners.delete(listener);
    if (breakpointListeners.size === 0 && breakpointMediaCleanup) breakpointMediaCleanup();
  };
};

const getBreakpointServerSnapshot = () => 'base';

const useActiveBreakpoint = () =>
  useSyncExternalStore(subscribeToBreakpoint, getActiveBreakpoint, getBreakpointServerSnapshot);

// Mirrors CSS cascade order of Tailwind's mobile-first breakpoint classes:
// at a given active breakpoint, the largest defined value at-or-below it wins.
const cascade = (active, base, sm, md, lg, xl) => {
  if (active === 'xl') return xl ?? lg ?? md ?? sm ?? base;
  if (active === 'lg') return lg ?? md ?? sm ?? base;
  if (active === 'md') return md ?? sm ?? base;
  if (active === 'sm') return sm ?? base;
  return base;
};

const JUSTIFY_CONTENT_MAP = {
  start: 'flex-start',
  end: 'flex-end',
  center: 'center',
  between: 'space-between',
  around: 'space-around',
  evenly: 'space-evenly',
};

const ALIGN_ITEMS_MAP = {
  start: 'flex-start',
  end: 'flex-end',
  center: 'center',
  baseline: 'baseline',
  stretch: 'stretch',
};

const FLEX_DIRECTION_MAP = {
  row: 'row',
  col: 'column',
  'row-reverse': 'row-reverse',
  'col-reverse': 'column-reverse',
};

export const Flex = ({
  flexDirection = 'row',
  justifyContent = 'between',
  alignItems = 'center',
  className,
  style,
  children,
  ...rest
}) => (
  <div
    className={className}
    style={{
      display: 'flex',
      width: '100%',
      // Tremor looks these up in its own Tailwind-class maps and silently
      // emits no class at all for an unrecognized value (e.g. the "top"/
      // "flex-start"/"space-between" typos found elsewhere in this codebase
      // — Tremor only recognizes start/end/center/between/around/evenly and
      // start/end/center/baseline/stretch respectively). Leaving the style
      // property undefined here reproduces that: no CSS rule is emitted, so
      // the browser's native flexbox default (stretch for align-items, normal
      // for justify-content) applies, same as it did under real Tremor.
      flexDirection: FLEX_DIRECTION_MAP[flexDirection],
      justifyContent: JUSTIFY_CONTENT_MAP[justifyContent],
      alignItems: ALIGN_ITEMS_MAP[alignItems],
      ...style,
    }}
    {...rest}
  >
    {children}
  </div>
);

const decorationBorderStyle = (decoration, decorationColor) => {
  if (!decoration) return null;
  const border = `4px solid ${resolveColor(decorationColor)}`;
  switch (decoration) {
    case 'top':
      return { borderTop: border };
    case 'bottom':
      return { borderBottom: border };
    case 'left':
      return { borderLeft: border };
    case 'right':
      return { borderRight: border };
    default:
      return null;
  }
};

export const Card = ({ decoration, decorationColor, className, style, children, ...rest }) => (
  <div
    className={className}
    style={{
      ...defaultCardStyle,
      position: 'relative',
      width: '100%',
      textAlign: 'left',
      ...decorationBorderStyle(decoration, decorationColor),
      ...style,
    }}
    {...rest}
  >
    {children}
  </div>
);

export const Grid = ({
  numItems = 1,
  numItemsSm,
  numItemsMd,
  numItemsLg,
  numItemsXl,
  className,
  style,
  children,
  ...rest
}) => {
  const active = useActiveBreakpoint();
  const cols = cascade(active, numItems, numItemsSm, numItemsMd, numItemsLg, numItemsXl);

  return (
    <div
      className={className}
      style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, ...style }}
      {...rest}
    >
      {children}
    </div>
  );
};

export const Col = ({
  numColSpan = 1,
  numColSpanSm,
  numColSpanMd,
  numColSpanLg,
  numColSpanXl,
  className,
  style,
  children,
  ...rest
}) => {
  const active = useActiveBreakpoint();
  const span = cascade(active, numColSpan, numColSpanSm, numColSpanMd, numColSpanLg, numColSpanXl);

  return (
    <div className={className} style={{ gridColumn: `span ${span} / span ${span}`, ...style }} {...rest}>
      {children}
    </div>
  );
};

export const Title = ({ color, className, style, children, ...rest }) => (
  <p
    className={className}
    style={{
      fontSize: 18,
      lineHeight: '28px',
      fontWeight: 500,
      color: color ? resolveColor(color) : '#111827',
      ...style,
    }}
    {...rest}
  >
    {children}
  </p>
);

export const Subtitle = ({ color, className, style, children, ...rest }) => (
  <p className={className} style={{ color: color ? resolveColor(color) : '#374151', ...style }} {...rest}>
    {children}
  </p>
);

export const Metric = ({ color, className, style, children, ...rest }) => (
  <p
    className={className}
    style={{
      fontSize: 30,
      lineHeight: '36px',
      fontWeight: 600,
      color: color ? resolveColor(color) : '#111827',
      ...style,
    }}
    {...rest}
  >
    {children}
  </p>
);

export const Text = ({ color, className, style, children, ...rest }) => (
  <p
    className={className}
    style={{ fontSize: 14, lineHeight: '20px', color: color ? resolveColor(color) : '#6b7280', ...style }}
    {...rest}
  >
    {children}
  </p>
);

export const Bold = ({ className, style, children, ...rest }) => (
  <b className={className} style={style} {...rest}>
    {children}
  </b>
);

// Accepts either a bare react-icon component reference (icon={IconEdit}, the
// shape every call site in this codebase uses) or an already-instantiated
// element (icon={<IconEdit/>}), matching whichever a caller happens to pass.
const renderIcon = (icon, extraStyle) => {
  if (!icon) return null;
  if (React.isValidElement(icon)) {
    return React.cloneElement(icon, { style: { flexShrink: 0, ...icon.props.style, ...extraStyle } });
  }
  const IconComponent = icon;
  return <IconComponent style={{ flexShrink: 0, ...extraStyle }} />;
};

// Tremor's own brand-blue default (tailwind.config.js tremor.brand.DEFAULT)
// used whenever no `color` prop is given at all — distinct from resolveColor's
// slate fallback, which is for a genuinely unrecognized colour *name*.
const DEFAULT_BRAND_COLOR = '#3b82f6';

const BUTTON_SIZE_MAP = { xs: 'xs', sm: 'sm', md: 'md', lg: 'lg', xl: 'lg' }; // rsuite has no "xl"

// Tremor's getButtonColors(variant, color) (Button/styles.js): "primary" is a
// solid fill with white text; "secondary" and "light" are both text+border-only,
// differing in whether the border/shadow decoration is drawn at all.
const buttonVariantStyle = (variant, resolved) => {
  if (variant === 'light') {
    return { backgroundColor: 'transparent', border: 'none', boxShadow: 'none', color: resolved };
  }
  if (variant === 'secondary') {
    return { backgroundColor: 'transparent', border: `1px solid ${resolved}`, color: resolved };
  }
  return { backgroundColor: resolved, border: `1px solid ${resolved}`, color: '#fff' };
};

export const Button = ({
  icon,
  iconPosition = 'left',
  size = 'sm',
  color,
  variant = 'primary',
  disabled,
  loading,
  children,
  className,
  style,
  ...rest
}) => {
  const resolved = color ? resolveColor(color) : DEFAULT_BRAND_COLOR;
  const iconElement = renderIcon(icon);
  const isDisabled = disabled || loading;

  return (
    <RsuiteButton
      size={BUTTON_SIZE_MAP[size] ?? 'sm'}
      disabled={isDisabled}
      loading={loading}
      className={className}
      style={{
        ...buttonVariantStyle(variant, resolved),
        // rsuite's own disabled-state dimming only fires for its "primary"
        // appearance class, which this component never sets (color is done
        // via inline style instead) — so without this, a disabled button
        // renders visually identical to an enabled one. Matches Tremor's own
        // explicit "opacity-50 cursor-not-allowed" disabled treatment.
        ...(isDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : null),
        // Tremor's real Button has "shrink-0" baked in so it never shrinks
        // inside a flex row; rsuite's Button has no such default, and
        // combined with its own overflow:hidden + white-space:nowrap, a
        // shrunk button clips its text instead of staying full width.
        flexShrink: 0,
        ...style,
      }}
      startIcon={iconPosition === 'right' ? undefined : iconElement}
      endIcon={iconPosition === 'right' ? iconElement : undefined}
      {...rest}
    >
      {children}
    </RsuiteButton>
  );
};

const ICON_SIZE_PX = { xs: 16, sm: 20, md: 20, lg: 24, xl: 28 };

export const Icon = ({ icon, color, size = 'sm', className, style, ...rest }) => {
  if (!icon) return null;
  if (React.isValidElement(icon)) return icon;
  const px = ICON_SIZE_PX[size] ?? ICON_SIZE_PX.sm;
  const IconComponent = icon;
  return (
    <IconComponent
      className={className}
      style={{ color: color ? resolveColor(color) : undefined, width: px, height: px, ...style }}
      {...rest}
    />
  );
};

// Reproduces Tremor's <Badge>: a pale colour-tint fill with darker same-hue
// text (see tremorBadgeStyle), NOT a solid fill. When no color is given, falls
// back to Tremor's own brand default (bg-tremor-brand-faint / text-emphasis),
// i.e. the brand blue tint. The icon inherits the pill's text colour via
// currentColor, matching Tremor.
export const Badge = ({ icon, color, children, className, style, ...rest }) => (
  <span className={className} style={{ ...tremorBadgeStyle(color || 'blue'), ...style }} {...rest}>
    {renderIcon(icon)}
    {children != null && children !== '' && <span>{children}</span>}
  </span>
);

// Deliberately NOT rsuite's Message: Message nests children three levels deep
// (container > content > body), which breaks call sites whose own CSS (e.g.
// NoteComponents/css/Note.css's ".note") targets *direct* children with
// display:flex/justify-content:space-between. Tremor's real Callout is a much
// flatter flex-column div (header row, then a single body wrapper) — this
// mirrors that exact shape so existing per-call-site CSS keeps working.
export const Callout = ({ title, titleColor, icon, color, className, style, children, ...rest }) => {
  const colorToken = color || DEFAULT_BRAND_COLOR;
  // Tremor's Callout pairs a pale tinted background (bg-{color}-500 at 10%
  // opacity, not a lighter shade number) with the *darker* 700-tier text and
  // border — resolveColor() alone only gives the 500-tier, which read as
  // "text same shade as the accent border" rather than the intended
  // beige-background/brown-text look.
  const dark = resolveColorDark(colorToken);
  const tint = resolveColorTint(colorToken);
  // Title matches the body/border colour by default (Tremor's own Callout
  // uses one colour throughout) — titleColor is opt-in for call sites that
  // deliberately want a different accent for just the title (e.g.
  // CreateNote.js's "New Note" heading, confirmed a two-tone design against
  // a real screenshot of the original, not a Callout-wide default).
  const resolvedTitleColor = titleColor ? resolveColor(titleColor) : dark;
  return (
    <div
      className={className}
      style={{
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRadius: 8,
        borderLeft: `4px solid ${dark}`,
        padding: '12px 12px 12px 16px',
        backgroundColor: tint,
        color: dark,
        ...style,
      }}
      {...rest}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
        {renderIcon(icon, { flexShrink: 0, width: 20, height: 20, marginRight: 6 })}
        <h4 style={{ fontWeight: 600, margin: 0, color: resolvedTitleColor }}>{title}</h4>
      </div>
      <p style={{ overflowY: 'auto', margin: 0, marginTop: children ? 8 : 0 }}>{children}</p>
    </div>
  );
};

// Tabs (Batch 5): preserves Tremor's exact index-based TabGroup/TabList/Tab
// API (defaultIndex/index/onIndexChange on TabGroup; variant/color on
// TabList; plain children/icon on Tab) so neither real call site
// (HomeView.js, TrendsView.js) needed any JSX changes at all, only their
// import source — same zero-JSX-diff approach as every other export here.
// Backed internally by rsuite's real Nav/Nav.Item (a key-based model), with
// the index<->key translation done purely by each Tab's POSITION within its
// TabList (React.Children.toArray + array index), exactly mirroring how
// Tremor's own HeadlessUI-based Tab.List auto-assigns indices by DOM order.
// This matters: TrendsView.js's real Tab elements carry extra `index=`/
// `value=`/`key=` props that look like they should drive the mapping, but
// none of those are part of Tremor's actual Tab API (confirmed against its
// source — Tab.js destructures only icon/className/children) and Tremor
// itself ignores them, relying purely on position. Reading them here instead
// of position would have been a different, wrong design that happened to
// look right only because orpPhValues.orp/ph (0/1) coincidentally match
// those Tabs' written order.
const TabGroupContext = createContext({ selectedIndex: 0, onSelect: () => {} });

export const TabGroup = ({ index, defaultIndex = 0, onIndexChange, children, className, style, ...rest }) => {
  const [uncontrolledIndex, setUncontrolledIndex] = useState(defaultIndex);
  const selectedIndex = index ?? uncontrolledIndex;
  const handleSelect = (newIndex) => {
    if (index === undefined) setUncontrolledIndex(newIndex);
    onIndexChange?.(newIndex);
  };
  return (
    <div className={className} style={{ width: '100%', ...style }} {...rest}>
      <TabGroupContext.Provider value={{ selectedIndex, onSelect: handleSelect }}>{children}</TabGroupContext.Provider>
    </div>
  );
};

// variant="solid" values (padding/radius/gap/background/border/shadow) read
// directly off @tremor/react's compiled TabList.js/Tab.js classes and
// tailwind.config.js's tremor color tokens, not guessed. variant="line" is
// Tremor's own default but isn't used by either real call site today (both
// pass "solid" explicitly) — kept as a reasonable but unverified fallback so
// a future TabList without an explicit variant doesn't render unstyled.
export const TabList = ({ variant = 'line', color, children, className, style, ...rest }) => {
  const { selectedIndex, onSelect } = useContext(TabGroupContext);
  // React.Children.toArray keeps any stray string/number children (e.g. JSX
  // whitespace/text between <Tab> elements) — cloneElement below throws on
  // those, so only real elements are kept, and indexed by their position
  // among each other (matching Tremor's own DOM-order-based indexing).
  const items = React.Children.toArray(children).filter(React.isValidElement);
  const isSolid = variant === 'solid';
  return (
    <RsuiteNav
      activeKey={selectedIndex}
      onSelect={(key) => {
        // rsuite's onSelect passes back whatever eventKey type was set on
        // the Nav.Item — a string in some rsuite versions/paths — but the
        // index<->key contract here (and TabGroup's selectedIndex ===
        // __index comparison) is strictly numeric.
        const numericKey = Number(key);
        if (!Number.isNaN(numericKey)) onSelect(numericKey);
      }}
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: isSolid ? 2 : 0,
        borderRadius: isSolid ? 8 : 0,
        gap: isSolid ? 6 : 16,
        backgroundColor: isSolid ? '#f3f4f6' : 'transparent',
        borderBottom: isSolid ? 'none' : '1px solid #e5e7eb',
        ...style,
      }}
      {...rest}
    >
      {items.map((child, i) =>
        React.cloneElement(child, {
          key: child.key ?? i,
          __index: i,
          __variant: variant,
          __color: color,
        }),
      )}
    </RsuiteNav>
  );
};

// index/value: real call sites (e.g. TrendsView.js) pass these leftover
// props from a pre-migration design, but Tremor's actual Tab component
// (confirmed against its source) destructures only icon/className/children
// and ignores them — so they're accepted and dropped here too, rather than
// leaking into the DOM via ...rest/RsuiteNav.Item.
export const Tab = ({
  icon,
  children,
  index: _index,
  value: _value,
  __index = 0,
  __variant = 'line',
  __color,
  className,
  style,
  ...rest
}) => {
  const { selectedIndex } = useContext(TabGroupContext);
  const isSelected = selectedIndex === __index;
  const resolved = __color ? resolveColor(__color) : DEFAULT_BRAND_COLOR;
  const isSolid = __variant === 'solid';

  const variantStyle = isSolid
    ? {
        border: `1px solid ${isSelected ? '#e5e7eb' : 'transparent'}`,
        borderRadius: 6,
        padding: '4px 10px',
        backgroundColor: isSelected ? '#ffffff' : 'transparent',
        boxShadow: isSelected ? '0 1px 2px 0 rgb(0 0 0 / 0.05)' : 'none',
      }
    : {
        borderBottom: `2px solid ${isSelected ? resolved : 'transparent'}`,
        padding: '8px 2px',
        marginBottom: -1,
      };

  return (
    <RsuiteNav.Item
      eventKey={__index}
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        cursor: 'pointer',
        fontSize: 14,
        whiteSpace: 'nowrap',
        color: isSelected ? resolved : '#6b7280',
        ...variantStyle,
        ...style,
      }}
      {...rest}
    >
      {renderIcon(icon, { width: 20, height: 20, marginRight: children ? 8 : 0 })}
      {children}
    </RsuiteNav.Item>
  );
};

// AreaChart/LineChart/DonutChart (Batch 6) — backed by Recharts (owner
// decision, not @rsuite/charts). Prop shapes modeled directly off @tremor/
// react's own compiled AreaChart.js/LineChart.js/DonutChart.js and their
// shared common/utils.js (constructCategoryColors, getYAxisDomain), not
// guessed: category→color mapping is purely positional (categories[i] pairs
// with colors[i]), minValue/maxValue/autoMinValue feed the Y-axis domain as
// [autoMinValue ? "auto" : (minValue ?? 0), maxValue ?? "auto"], and
// rotateLabelX/tickGap/startEndOnly map onto Recharts' XAxis angle/dy/height,
// minTickGap, and interval="preserveStartEnd"+explicit first/last ticks
// respectively. Recharts' real per-series shape (one <Line>/<Area> element
// per category) is fundamentally different from Tremor's single
// categories-array prop, so this is a genuine translation, not a rename.
const CHART_TICK_STYLE = { fontSize: 12, fill: '#6b7280' };
const CHART_GRID_STROKE = '#e5e7eb';

// Tailwind's default height scale (unmodified by tailwind.config.js) for the
// specific classes real call sites pass — e.g. "mt-6 h-40". Tailwind itself
// is still active in the app until the Batch 7 teardown, so this only needs
// to cover className-driven *height*; other utility classes (mt-6, etc.)
// keep working via Tailwind's own stylesheet same as before.
const HEIGHT_CLASS_PX = { 'h-40': 160, 'h-56': 224, 'h-64': 256, 'h-72': 288, 'h-80': 320 };

const extractHeightPx = (className, fallback) => {
  if (!className) return fallback;
  const match = className.split(/\s+/).find((token) => HEIGHT_CLASS_PX[token]);
  return match ? HEIGHT_CLASS_PX[match] : fallback;
};

const xAxisPaddingFor = (showXAxis, showYAxis, startEndOnly) =>
  (!showXAxis && !showYAxis) || (startEndOnly && !showYAxis) ? 0 : 20;

const yAxisDomainFor = (autoMinValue, minValue, maxValue) => [
  autoMinValue ? 'auto' : (minValue ?? 0),
  maxValue ?? 'auto',
];

const seriesColor = (colors, i) => resolveColor(colors[i]);

// Default tooltip content, modeled on Tremor's real ChartTooltipFrame/
// ChartTooltipRow (@tremor/react/dist/components/chart-elements/common/
// ChartTooltip.js): a bordered card, a header row with the x-axis label
// under a bottom-border separator, then one row per series — a small
// coloured ring "bullet" (border-2, white ring, drop shadow) next to the
// series name, value right-aligned. Lost in the first Batch 6 pass, which
// left Recharts' plain unstyled default tooltip in place — restored here as
// the shared default for both AreaChart and LineChart.
const CHART_TOOLTIP_FRAME_STYLE = {
  borderRadius: 8,
  border: '1px solid #e5e7eb',
  background: '#ffffff',
  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
  fontSize: 14,
  overflow: 'hidden',
};

const DefaultChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const rows = payload.filter((entry) => entry.type !== 'none');
  return (
    <div style={CHART_TOOLTIP_FRAME_STYLE}>
      <div style={{ borderBottom: '1px solid #e5e7eb', padding: '8px 16px' }}>
        <p style={{ margin: 0, fontWeight: 500, color: '#374151' }}>{label}</p>
      </div>
      <div style={{ padding: '8px 16px', display: 'flex', flexDirection: 'column', gap: 4 }}>
        {rows.map((entry, i) => (
          <div
            key={`${entry.dataKey}-${i}`}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 32 }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span
                style={{
                  flexShrink: 0,
                  borderRadius: '9999px',
                  border: '2px solid #ffffff',
                  boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
                  height: 12,
                  width: 12,
                  backgroundColor: entry.color,
                }}
              />
              <p style={{ margin: 0, color: '#6b7280', whiteSpace: 'nowrap' }}>{entry.name}</p>
            </div>
            <p
              style={{
                margin: 0,
                fontWeight: 500,
                color: '#374151',
                whiteSpace: 'nowrap',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {entry.value}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

const ChartXAxis = ({ data, index, showXAxis, showYAxis, startEndOnly, tickGap, rotateLabelX }) => (
  <XAxis
    dataKey={index}
    hide={!showXAxis}
    interval={startEndOnly ? 'preserveStartEnd' : 'equidistantPreserveStart'}
    ticks={startEndOnly && data.length ? [data[0][index], data[data.length - 1][index]] : undefined}
    minTickGap={tickGap}
    angle={rotateLabelX?.angle}
    dy={rotateLabelX?.verticalShift}
    height={rotateLabelX?.xAxisHeight}
    padding={{
      left: xAxisPaddingFor(showXAxis, showYAxis, startEndOnly),
      right: xAxisPaddingFor(showXAxis, showYAxis, startEndOnly),
    }}
    tick={CHART_TICK_STYLE}
    axisLine={false}
    tickLine={false}
  />
);

const ChartYAxis = ({ showYAxis, yAxisWidth, autoMinValue, minValue, maxValue, allowDecimals }) => (
  <YAxis
    hide={!showYAxis}
    width={yAxisWidth}
    type="number"
    domain={yAxisDomainFor(autoMinValue, minValue, maxValue)}
    tick={CHART_TICK_STYLE}
    axisLine={false}
    tickLine={false}
    allowDecimals={allowDecimals}
  />
);

export const AreaChart = ({
  data = [],
  categories = [],
  index,
  colors = [],
  showAnimation = false,
  animationDuration = 900,
  showLegend = true,
  showGridLines = true,
  showXAxis = true,
  showYAxis = true,
  yAxisWidth = 56,
  curveType = 'linear',
  connectNulls = false,
  startEndOnly = false,
  autoMinValue = false,
  minValue,
  maxValue,
  allowDecimals = true,
  tickGap = 5,
  rotateLabelX,
  customTooltip,
  // Optional { dataKey: displayLabel }. Recharts addresses data by `dataKey` but shows
  // `name` in the legend and tooltip, so this relabels both without touching the keys —
  // which matters here because a series key is also the token the picker stores.
  categoryLabels = {},
  className,
  style,
}) => {
  const gradientId = useId();
  return (
    <div className={className} style={{ width: '100%', height: extractHeightPx(className, 320), ...style }}>
      <ResponsiveContainer width="100%" height="100%">
        <RechartsAreaChart data={data}>
          {showGridLines && <CartesianGrid stroke={CHART_GRID_STROKE} horizontal vertical={false} />}
          <ChartXAxis
            data={data}
            index={index}
            showXAxis={showXAxis}
            showYAxis={showYAxis}
            startEndOnly={startEndOnly}
            tickGap={tickGap}
            rotateLabelX={rotateLabelX}
          />
          <ChartYAxis
            showYAxis={showYAxis}
            yAxisWidth={yAxisWidth}
            autoMinValue={autoMinValue}
            minValue={minValue}
            maxValue={maxValue}
            allowDecimals={allowDecimals}
          />
          <RechartsTooltip content={customTooltip ?? DefaultChartTooltip} />
          {showLegend && <RechartsLegend verticalAlign="top" align="right" height={36} iconSize={8} />}
          <defs>
            {categories.map((category, i) => (
              <linearGradient key={category} id={`${gradientId}-${i}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={seriesColor(colors, i)} stopOpacity={0.4} />
                <stop offset="95%" stopColor={seriesColor(colors, i)} stopOpacity={0} />
              </linearGradient>
            ))}
          </defs>
          {categories.map((category, i) => {
            const color = seriesColor(colors, i);
            return (
              <Area
                key={category}
                type={curveType}
                dataKey={category}
                name={categoryLabels[category] ?? category}
                stroke={color}
                fill={`url(#${gradientId}-${i})`}
                legendType="circle"
                strokeWidth={2}
                connectNulls={connectNulls}
                isAnimationActive={showAnimation}
                animationDuration={animationDuration}
              />
            );
          })}
        </RechartsAreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export const LineChart = ({
  data = [],
  categories = [],
  index,
  colors = [],
  showAnimation = false,
  animationDuration = 900,
  showLegend = true,
  showGridLines = true,
  showXAxis = true,
  showYAxis = true,
  yAxisWidth = 56,
  curveType = 'linear',
  connectNulls = false,
  startEndOnly = false,
  autoMinValue = false,
  minValue,
  maxValue,
  allowDecimals = true,
  tickGap = 5,
  rotateLabelX,
  customTooltip,
  className,
  style,
}) => (
  <div className={className} style={{ width: '100%', height: extractHeightPx(className, 320), ...style }}>
    <ResponsiveContainer width="100%" height="100%">
      <RechartsLineChart data={data}>
        {showGridLines && <CartesianGrid stroke={CHART_GRID_STROKE} horizontal vertical={false} />}
        <ChartXAxis
          data={data}
          index={index}
          showXAxis={showXAxis}
          showYAxis={showYAxis}
          startEndOnly={startEndOnly}
          tickGap={tickGap}
          rotateLabelX={rotateLabelX}
        />
        <ChartYAxis
          showYAxis={showYAxis}
          yAxisWidth={yAxisWidth}
          autoMinValue={autoMinValue}
          minValue={minValue}
          maxValue={maxValue}
          allowDecimals={allowDecimals}
        />
        <RechartsTooltip content={customTooltip ?? DefaultChartTooltip} />
        {showLegend && <RechartsLegend verticalAlign="top" align="right" height={36} iconSize={8} />}
        {categories.map((category, i) => (
          <Line
            key={category}
            type={curveType}
            dataKey={category}
            name={category}
            stroke={seriesColor(colors, i)}
            strokeWidth={2}
            dot={false}
            legendType="circle"
            connectNulls={connectNulls}
            isAnimationActive={showAnimation}
            animationDuration={animationDuration}
          />
        ))}
      </RechartsLineChart>
    </ResponsiveContainer>
  </div>
);

// height= isn't a real DonutChart prop upstream either — Tremor's own
// DonutChart doesn't destructure it, so it fell through to the outer div as
// a raw (invalid, no-op) HTML attribute. StatsView.js's
// `height={showList ? "h-56" : "h-80"}` therefore never actually changed
// anything; the chart always rendered at Tremor's own DonutChart default of
// h-40 (160px). Reproduced faithfully here: the prop is accepted (so the
// call site doesn't need to change) and intentionally ignored.
// Single-row counterpart to DefaultChartTooltip, modeled on Tremor's real
// DonutChartTooltip.js — which itself just reuses ChartTooltipFrame/Row for
// one row (no header/separator, since there's no x-axis label for a slice).
const DefaultDonutTooltip = ({ active, payload }) => {
  const entry = payload?.[0];
  if (!active || !entry) return null;
  return (
    <div style={CHART_TOOLTIP_FRAME_STYLE}>
      <div
        style={{
          padding: '8px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 32,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              flexShrink: 0,
              borderRadius: '9999px',
              border: '2px solid #ffffff',
              boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
              height: 12,
              width: 12,
              backgroundColor: entry.color,
            }}
          />
          <p style={{ margin: 0, color: '#6b7280', whiteSpace: 'nowrap' }}>{entry.name}</p>
        </div>
        <p
          style={{
            margin: 0,
            fontWeight: 500,
            color: '#374151',
            whiteSpace: 'nowrap',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {entry.value}
        </p>
      </div>
    </div>
  );
};

export const DonutChart = ({
  data = [],
  category = 'value',
  index = 'name',
  colors = [],
  variant = 'donut',
  showAnimation = false,
  animationDuration = 900,
  showTooltip = true,
  className,
  style,
  height: _ignoredHeight,
}) => {
  const isDonut = variant === 'donut';
  return (
    <div className={className} style={{ width: '100%', height: extractHeightPx(className, 160), ...style }}>
      <ResponsiveContainer width="100%" height="100%">
        <RechartsPieChart>
          <Pie
            data={data}
            dataKey={category}
            nameKey={index}
            cx="50%"
            cy="50%"
            startAngle={90}
            endAngle={-270}
            innerRadius={isDonut ? '75%' : '0%'}
            outerRadius="100%"
            isAnimationActive={showAnimation}
            animationDuration={animationDuration}
          >
            {data.map((entry, i) => (
              <Cell key={entry[index] ?? i} fill={seriesColor(colors, i)} />
            ))}
          </Pie>
          {showTooltip && <RechartsTooltip content={DefaultDonutTooltip} />}
        </RechartsPieChart>
      </ResponsiveContainer>
    </div>
  );
};
